import clsx from "clsx";

import { LABEL_COLORS } from "features/labels/constants";
import useAllLabels from "features/labels/datahooks/useAllLabels";
import { useLeadsFacet } from "features/leads/datahooks";
import useLeadsSearchParams from "features/leads/hooks/useLeadsSearchParams";
import type { LeadsSearchParams } from "features/leads/types";

import ProfileImage from "common/components/ui/ProfileImage";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useWorkspaceAccounts from "common/datahooks/useWorkspaceAccounts";
import { getSearchParamFacetKey } from "common/helpers/utils";
import useSortedFacets from "common/hooks/useSortedFacets";

import ListSelection from "../../Filters/ListSelection";

import type { FiltersProps } from "./FiltersSelection";

interface TypeaheadValuesProps extends FiltersProps {
  searchKey: keyof Omit<LeadsSearchParams<never>, "keywords" | "favorite">;
  query: string;
}

export default function TypeaheadValues({
  searchKey,
  query,
  searchParams,
  setSearchParams,
}: TypeaheadValuesProps) {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { toggleSearchParam } = useLeadsSearchParams(
    searchParams,
    setSearchParams,
  );
  const { workspaceAccounts } = useWorkspaceAccounts(workspaceId);
  const { allLabels } = useAllLabels("workspace");

  const selectedFacets = searchParams[searchKey];
  const { facetData } = useLeadsFacet(
    getSearchParamFacetKey(searchKey),
    searchParams,
  );
  const sortedFacets = useSortedFacets(facetData || [], selectedFacets);

  const lowercasedQuery = query.toLowerCase();
  // selected facets are displayed as tags, so they are left out of the selection
  const selectionFacets = sortedFacets.filter(
    ({ id, name }) =>
      !selectedFacets.some((facet) => facet.id === id) &&
      name.toLowerCase().includes(lowercasedQuery),
  );

  return (
    <ListSelection
      idKey="id"
      renderLabel={({ id, name }) => {
        if (searchKey === "lead_of") {
          const account = workspaceAccounts?.find(
            (account) => account.id === id,
          );
          if (account) {
            return account.full_name;
          }
        }
        return name;
      }}
      countKey="target_count"
      renderIcon={({ id }) => {
        if (searchKey === "lead_of") {
          const account = workspaceAccounts?.find(
            (account) => account.id === id,
          );
          if (account) {
            return <ProfileImage size="sm" src={account.picture_url} />;
          }
        } else if (searchKey === "labels") {
          const label = allLabels?.find((label) => label.id === id);

          if (label) {
            return (
              <div
                className={clsx(
                  "size-6 rounded",
                  LABEL_COLORS[label.color].bgClassName,
                )}
              />
            );
          }
        }
        return null;
      }}
      options={selectionFacets}
      onSelect={(facet) => toggleSearchParam(searchKey, facet)}
    />
  );
}
